import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Building2, Users, Clock, HardDrive, ArrowRight, ExternalLink } from 'lucide-react';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function Dashboard() {
  const [tenants, setTenants] = useState<any[]>([]);
  const [waitlist, setWaitlist] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<any[]>('/tenants'),
      api.get<any[]>('/waitlist'),
    ]).then(([t, w]) => { setTenants(t); setWaitlist(w); })
      .finally(() => setIsLoading(false));
  }, []);

  const active = tenants.filter(t => t.status === 'active').length;
  const pending = waitlist.filter(u => u.status === 'waitlist').length;
  const totalStorage = tenants.reduce((s: number, t: any) => s + (t.storage_used_bytes || 0), 0);

  const stats = [
    { label: 'Totaal tenants', value: tenants.length, icon: Building2, color: 'bg-brand-100 text-brand-600' },
    { label: 'Actief', value: active, icon: Building2, color: 'bg-green-100 text-green-600' },
    { label: 'Wachtlijst', value: pending, icon: Clock, color: 'bg-amber-100 text-amber-600' },
    { label: 'Totale opslag', value: formatBytes(totalStorage), icon: HardDrive, color: 'bg-blue-100 text-blue-600' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Platform overzicht</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <div key={s.label} className="card p-5">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">{s.label}</p>
                {isLoading ? (
                  <div className="mt-1 h-7 bg-gray-100 rounded animate-pulse w-16" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent tenants */}
        <div className="card">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">Recente tenants</h2>
            <Link to="/admin/tenants" className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
              Alles bekijken <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="p-2">
            {isLoading ? (
              <div className="space-y-1 p-4">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-gray-50 rounded-lg animate-pulse" />)}</div>
            ) : tenants.length === 0 ? (
              <p className="text-sm text-gray-400 p-6 text-center">Nog geen tenants.</p>
            ) : (
              <ul>
                {[...tenants].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 5).map(t => (
                  <li key={t.id}>
                    <Link
                      to={`/admin/tenants/${t.id}`}
                      className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">
                          {t.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t.name}</p>
                          <p className="text-xs text-gray-400">{t.domains?.[0] || t.slug}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>{t.status}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Pending waitlist */}
        <div className="card">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">Wachtlijst</h2>
            <Link to="/admin/waitlist" className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
              Beheren <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="p-2">
            {isLoading ? (
              <div className="space-y-1 p-4">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-50 rounded-lg animate-pulse" />)}</div>
            ) : pending === 0 ? (
              <p className="text-sm text-gray-400 p-6 text-center">Geen wachtende aanvragen.</p>
            ) : (
              <ul>
                {waitlist.filter(u => u.status === 'waitlist').slice(0, 5).map(u => (
                  <li key={u.id} className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Users className="w-4 h-4 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{u.full_name || u.name}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">
                      {u.desired_site_name && `${u.desired_site_name}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
